import Fuse from "fuse.js";
import { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Option } from "~/lib/constants";
import { cn } from "~/lib/utils";

import { Button } from "../button";
import { Icon } from "../icon";
import { Input } from "../input";

export type SearchableDropdownProps = {
  className?: string;
  placeholder?: string;
  options: Option[];
  readOnly?: boolean;
  searchPlaceholder?: string;
  onChange?: (option: Option) => void;
  defaultValue?: Option;
  menuItemComponent?: React.FC<{
    option: Option;
    isSelected?: boolean;
  }>;
  prefix?: React.ReactNode;
  value?: Option;
  emptyStateText?: string;
};

const fuseOptions = {
  isCaseSensitive: false,
  includeMatches: false,
  threshold: 0.3,
  keys: ["value", "label"],
};
export function SearchableDropdown({
  emptyStateText = "No data found",
  ...props
}: SearchableDropdownProps) {
  const [selectedOptions, setSelectedOptions] = useState<Option | undefined>(
    props?.defaultValue,
  );

  useEffect(() => {
    if (props.defaultValue) {
      setSelectedOptions(props.defaultValue);
    }
  }, [props.defaultValue]);

  const fuse = new Fuse(props.options ?? [], fuseOptions);

  const [searchValue, setSearchValue] = useState("");

  const results = fuse?.search(searchValue);
  const resultLists = results?.map((result) => result.item);
  // const filteredOptions = resultLists.length > 0 ? resultLists : props.options;
  const filteredOptions =
    resultLists.length > 0
      ? resultLists
      : searchValue
        ? resultLists
        : props.options;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={!props.readOnly && isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={props.readOnly}
          variant="outline"
          role="button"
          type="button"
          className={cn(
            "relative w-full justify-start px-3 text-sm focus:border-secondary-500 focus:ring-1 focus:ring-secondary-500",
            selectedOptions ? "text-primary-500" : "text-muted-foreground",
            props.className,

            {
              "cursor-not-allowed": props.readOnly,
            },
          )}
        >
          {selectedOptions?.label || props.placeholder}
          <div
            className={cn(
              "absolute right-3 flex h-full items-center justify-center",
              isOpen && "text-black",
            )}
          >
            <Icon name="ChevronDown" size={20} />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        onWheel={(event) => event.stopPropagation()}
        align="end"
        className="h-fit w-fit space-y-2 overflow-auto rounded-2xl px-3 py-2"
      >
        {props.options?.length > 0 && (
          <Input
            value={searchValue}
            prefix="Search"
            className="bg-neutral-100"
            placeholder={props.searchPlaceholder || "Search"}
            prefixClass={"w-4 h-4 text-muted-foreground"}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        )}
        <div className="flex max-h-72 flex-col gap-0 overflow-y-auto">
          {filteredOptions?.length > 0 ? (
            filteredOptions?.map((op, idx) => (
              <div
                key={idx}
                className="w-full cursor-pointer"
                onClick={() => {
                  if (props.onChange) {
                    props.onChange(op);
                  }

                  setSelectedOptions(op);
                  setIsOpen(false);
                }}
              >
                {props.menuItemComponent ? (
                  <props.menuItemComponent
                    option={op}
                    isSelected={op.value === selectedOptions?.value}
                  />
                ) : (
                  <div
                    className={cn(
                      "flex h-fit w-full gap-1.5 rounded-sm px-3 py-2 font-Medium text-sm",
                      op.value === selectedOptions?.value &&
                        "bg-neutral-200 font-medium",
                    )}
                  >
                    {props.prefix && props.prefix}
                    {op.label}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-center text-sm text-muted-foreground">
              {emptyStateText}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
